:root {
  color-scheme: light;
  --bg: #ecebe6;
  --panel: #fbfaf7;
  --surface: #ffffff;
  --surface-2: #f6f4ef;
  --ink: #1a1c1f;
  --ink-2: #3d4147;
  --muted: #6d7076;
  --faint: #9a9da3;
  --line: #e3e0d8;
  --line-strong: #d4d0c6;
  --accent: #1d5b84;
  --accent-soft: #e9f2f8;
  --pass: #1f6f5c;
  --pass-soft: #e6f2ee;
  --hold: #a96a12;
  --hold-soft: #fbf0dd;
  --drop: #6b7280;
  --drop-soft: #f0f0f1;
  --case: #7c4a6e;
  --case-soft: #f6ebf2;
  --shadow: 0 18px 50px rgba(24, 26, 30, 0.14), 0 2px 8px rgba(24, 26, 30, 0.06);
  --radius: 12px;
  --font: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Pretendard",
    "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif;
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  height: 100%;
}

body {
  margin: 0;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

button,
textarea {
  font: inherit;
  color: inherit;
}

button {
  cursor: pointer;
}

:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 6px;
}

.shell {
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 24px;
}

.panel {
  width: 400px;
  height: min(880px, calc(100dvh - 48px));
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* 판례 전문 리더 오버레이의 기준. */
  position: relative;
}

@media (max-width: 520px) {
  .shell {
    padding: 0;
  }
  .panel {
    width: 100%;
    height: 100dvh;
    border-radius: 0;
    border: 0;
  }
}

/* 사이드 패널 호스트: 데모 프레임 없이 꽉 채운다 */
body.is-extension .shell {
  padding: 0;
}

body.is-extension .panel {
  width: 100%;
  height: 100dvh;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* ── top ─────────────────────────────────────────── */

.top {
  height: 44px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  border-bottom: 1px solid var(--line);
}

.brand {
  display: flex;
  align-items: center;
  gap: 7px;
}

.mark {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--ink);
  color: var(--panel);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
}

.brand-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.beta {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--muted);
  border: 1px solid var(--line-strong);
  border-radius: 4px;
  padding: 1px 4px;
  transform: translateY(-1px);
}

.backend {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  color: var(--muted);
  border: 1px solid var(--line);
  background: var(--surface-2);
  border-radius: 999px;
  padding: 3px 8px;
  white-space: nowrap;
}

.led {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--faint);
}

.backend-webgpu .led {
  background: var(--pass);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--pass) 18%, transparent);
}

.backend-wasm .led {
  background: var(--hold);
}

.backend-mock .led {
  background: var(--accent);
}

/* ── query ───────────────────────────────────────── */

.query {
  flex: 0 0 auto;
  margin: 12px 12px 8px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 9px 9px 7px 11px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.query:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent);
}

.query textarea {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  resize: none;
  font-size: 13.5px;
  line-height: 1.55;
  min-height: 42px;
  max-height: 116px;
  padding: 0;
  display: block;
}

.query textarea::placeholder {
  color: var(--faint);
}

.query-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.kbd {
  font-size: 9.5px;
  color: var(--faint);
  letter-spacing: 0.03em;
}

.send {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: var(--ink);
  color: var(--panel);
  font-size: 14px;
  display: grid;
  place-items: center;
  transition: transform 100ms ease, opacity 100ms ease;
}

.send:disabled {
  opacity: 0.28;
  cursor: default;
}

.send:not(:disabled):active {
  transform: scale(0.94);
}

.ghost {
  border: 1px solid var(--line-strong);
  background: transparent;
  color: var(--muted);
  font-size: 10px;
  border-radius: 999px;
  padding: 3px 8px;
}

/* ── banners ─────────────────────────────────────── */

.banner {
  margin: 0 12px 8px;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.45;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.banner.error {
  background: color-mix(in srgb, #c0392b 12%, transparent);
  color: color-mix(in srgb, #c0392b 75%, var(--ink));
  border: 1px solid color-mix(in srgb, #c0392b 24%, transparent);
}

.banner.warn {
  background: var(--hold-soft);
  color: var(--hold);
  border: 1px solid color-mix(in srgb, var(--hold) 24%, transparent);
}

.banner.info {
  background: var(--accent-soft);
  color: color-mix(in srgb, var(--accent) 80%, var(--ink));
  border: 1px solid color-mix(in srgb, var(--accent) 22%, transparent);
}

.banner-action {
  margin-left: 8px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: inherit;
  font-weight: 700;
  text-decoration: underline;
  padding: 0;
}

.foot-link {
  color: var(--muted);
  text-decoration: none;
  border-bottom: 1px solid var(--line);
}

.foot-link:hover {
  color: var(--ink-2);
}

/* ── dossier ─────────────────────────────────────── */

.dossier-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: opacity 140ms ease;
}

.dossier-wrap.is-stale {
  opacity: 0.42;
  filter: saturate(0.5);
  pointer-events: none;
}

.dossier {
  padding: 2px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dossier-empty {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 12px 12px;
}

.dossier-empty .slot {
  height: 100%;
}

/* ── evidence-first 결과 (백엔드 SearchResponse) ───────── */

.results {
  padding: 2px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.results-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-2);
}

.results-count {
  color: var(--muted);
  font-weight: 500;
}

.result {
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  overflow: hidden;
}

.result.is-open {
  border-color: var(--line-strong);
}

.result-head-button {
  width: 100%;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  padding: 10px;
  border: 0;
  background: none;
  text-align: left;
}

.result-rank {
  padding-top: 2px;
  font-size: 10px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}

.result-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.result-title {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10.5px;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.result-snippet {
  font-size: 11px;
  color: var(--muted);
  line-height: 1.45;
}

.result-score {
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

.result-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

.result-tag {
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  color: var(--muted);
  white-space: nowrap;
}

.result-tag-statutes {
  background: var(--accent-soft);
  color: var(--accent);
  border-color: color-mix(in srgb, var(--accent) 20%, transparent);
}

.result-tag-citedCases {
  background: var(--case-soft);
  color: var(--case);
  border-color: color-mix(in srgb, var(--case) 20%, transparent);
}

.result-tag-directCites {
  background: var(--pass-soft);
  color: var(--pass);
  border-color: color-mix(in srgb, var(--pass) 20%, transparent);
}

.result-tag-empty {
  color: var(--faint);
}
.results-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.results-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 2px 0;
}
.results-section-count {
  color: var(--faint);
  font-size: 9.5px;
  font-variant-numeric: tabular-nums;
}
.result-tag-panel-direct {
  background: var(--accent-soft);
  color: var(--accent);
  border-color: color-mix(in srgb, var(--accent) 20%, transparent);
}
.result-tag-panel-related {
  color: var(--muted);
}
.result-tag-panel-case {
  background: var(--case-soft);
  color: var(--case);
  border-color: color-mix(in srgb, var(--case) 20%, transparent);
}
.result-match {
  font-size: 10px;
  color: var(--faint);
  overflow-wrap: anywhere;
}
.result-tag-provenance-ref {
  background: var(--case-soft);
  color: var(--case);
  border-color: color-mix(in srgb, var(--case) 20%, transparent);
}
.result-tag-provenance-body {
  color: var(--muted);
}

.result-body {
  padding: 0 10px 12px;
  border-top: 1px solid var(--line);
  margin-top: 2px;
}

.result-case {
  margin: 8px 0 0;
  font-size: 11px;
  color: var(--muted);
}

.result-evidence {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.result-evidence-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 8px;
  font-size: 10.5px;
}

.result-evidence-label {
  color: var(--muted);
  font-weight: 700;
}

.result-evidence-list {
  margin: 0;
  padding-left: 16px;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

/* ── 카드 본문: 판결문 근거 문장 ─────────────────────── */

.result-body-case {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-body-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.result-body-kicker {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--faint);
}

.result-copy {
  flex: 0 0 auto;
  padding: 3px 8px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--muted);
  font-size: 9.5px;
  font-weight: 650;
  white-space: nowrap;
}

.result-copy:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.result-copy-compact {
  padding: 2px 7px;
  font-size: 9px;
}

.result-sentences {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.result-sentence {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 9px;
  border-left: 2px solid var(--line);
}

.result-sentence-text {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.6;
  color: var(--ink-2);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: keep-all;
}

.result-sentence-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.result-sentence-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.result-sentence-label {
  font-size: 9.5px;
  color: var(--faint);
}

.result-evidence-kind {
  flex: 0 0 auto;
  padding: 1px 6px;
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: 8.5px;
  font-weight: 700;
  line-height: 1.45;
  white-space: nowrap;
}

.result-evidence-kind.is-citation {
  background: var(--accent-soft);
  color: var(--accent);
  border-color: color-mix(in srgb, var(--accent) 20%, transparent);
}

.result-evidence-kind.is-related {
  background: var(--case-soft);
  color: var(--case);
  border-color: color-mix(in srgb, var(--case) 20%, transparent);
}

.result-sentence-empty {
  margin: 0;
  font-size: 10.5px;
  color: var(--faint);
}

/* ── 전문 리더: 근거 문장 하이라이트 ─────────────────── */

.case-text-segmented .case-text-sentence {
  margin: 0 0 0.72em;
}

.case-text-segmented .case-text-sentence:last-child {
  margin-bottom: 0;
}

.case-text-sentence.is-highlight {
  margin-left: -10px;
  padding: 3px 10px;
  border-left: 3px solid var(--accent);
  border-radius: 6px;
  background: var(--accent-soft);
  scroll-margin-top: 64px;
}

/* 관련 문장은 인용과 구분되는 차분한 색. 경고처럼 보이지 않게 한다. */
.case-text-sentence.is-highlight.is-related {
  border-left-color: var(--case);
  background: var(--case-soft);
  font-weight: 500;
}

.case-reader-evidence-kinds,
.ws-detail-evidence-kinds {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  max-width: 34em;
  margin: 0 auto 10px;
}

.ws-detail-evidence-kinds {
  max-width: none;
  margin: 0 0 12px;
}


.lower {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: start;
}

.slot {
  min-width: 0;
}

.slot-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 4px 3px 6px;
}

.slot-title {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ink-2);
}

.sigil {
  color: var(--accent);
  font-size: 10px;
}

.slot-count {
  font-size: 9.5px;
  color: var(--faint);
}

.slot-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* ── cards ───────────────────────────────────────── */

.card,
.pair {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  animation: rise 180ms ease both;
}

.card-head,
.pair-head {
  display: grid;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 8px;
}

.card-head {
  grid-template-columns: 18px minmax(0, 1fr) auto;
  gap: 6px;
  align-items: start;
}

.card.is-open,
.pair.is-open {
  border-color: var(--line-strong);
  box-shadow: 0 2px 10px rgba(24, 26, 30, 0.05);
}

.kind {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  display: grid;
  place-items: center;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  flex: 0 0 auto;
}

.kind-statute {
  background: var(--accent-soft);
  color: var(--accent);
}

.kind-case {
  background: var(--case-soft);
  color: var(--case);
}

.card-main,
.pair-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-label {
  font-size: 11.5px;
  font-weight: 650;
  line-height: 1.3;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-snippet {
  font-size: 10px;
  line-height: 1.4;
  color: var(--muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* pair */

.pair-head {
  display: block;
  padding: 9px 9px 8px;
}

.pair-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.pair-row {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  gap: 6px;
  align-items: start;
}

.pair-link {
  color: var(--faint);
  font-size: 9px;
  line-height: 1;
  padding-left: 6px;
}

.pair-body {
  border-top: 1px solid var(--line);
}

.pair-body .expanded + .expanded {
  border-top: 1px dashed var(--line);
}

/* ── badges ──────────────────────────────────────── */

.badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border-radius: 999px;
  padding: 2px 5px;
  font-size: 8.5px;
  line-height: 1.35;
  font-weight: 700;
  border: 1px solid transparent;
  white-space: nowrap;
  flex: 0 0 auto;
}

.badge b {
  font-size: 9px;
}

.badge em {
  font-style: normal;
  font-weight: 600;
}

.badge-1 {
  color: var(--pass);
  background: var(--pass-soft);
  border-color: color-mix(in srgb, var(--pass) 22%, transparent);
}

.badge-0 {
  color: var(--hold);
  background: var(--hold-soft);
  border-color: color-mix(in srgb, var(--hold) 22%, transparent);
}

.badge--1 {
  color: var(--drop);
  background: var(--drop-soft);
  border-color: var(--line-strong);
}

.badge-judging {
  gap: 2px;
  padding: 4px 6px;
  background: var(--surface-2);
  border-color: var(--line);
}

.badge-none {
  color: var(--muted);
  background: var(--surface-2);
  border-color: var(--line);
  padding: 2px 6px;
}

.badge-judging .dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--faint);
  animation: blink 1.1s infinite ease-in-out;
}

.badge-judging .dot:nth-child(2) {
  animation-delay: 0.15s;
}

.badge-judging .dot:nth-child(3) {
  animation-delay: 0.3s;
}

/* ── expanded ────────────────────────────────────── */

.expanded {
  padding: 9px 10px 10px;
  background: var(--surface-2);
}

.expanded-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 9.5px;
  font-weight: 650;
  color: var(--ink-2);
  margin-bottom: 5px;
}

.expanded-meta .muted {
  font-weight: 400;
  color: var(--faint);
}

.original {
  margin: 0;
  font-size: 11px;
  line-height: 1.62;
  color: var(--ink-2);
  max-height: 220px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.probs {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.probs-empty {
  font-size: 10px;
  color: var(--faint);
}

.prob-row {
  display: grid;
  grid-template-columns: 26px 1fr 30px;
  gap: 5px;
  align-items: center;
}

.prob-name {
  font-size: 9px;
  color: var(--muted);
}

.prob-track {
  height: 3px;
  border-radius: 2px;
  background: var(--line);
  overflow: hidden;
}

.prob-fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  transition: width 240ms ease;
}

.tone-pass {
  background: var(--pass);
}
.tone-hold {
  background: var(--hold);
}
.tone-drop {
  background: var(--drop);
}

.prob-value {
  font-size: 8.5px;
  color: var(--faint);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.expanded-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 9px;
}

.copy {
  border: 1px solid var(--line-strong);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: 6px;
  font-size: 9.5px;
  padding: 3px 7px;
}

.hint {
  font-size: 8.5px;
  color: var(--faint);
}

/* ── empty / skeleton ────────────────────────────── */

.empty {
  padding: 44px 14px 20px;
  text-align: left;
}

.empty-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.empty-sub {
  margin: 5px 0 18px;
  font-size: 11px;
  line-height: 1.5;
  color: var(--muted);
}

.examples {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.examples button {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: 9px;
  padding: 9px 10px;
  font-size: 11px;
  line-height: 1.4;
}

.examples button span {
  color: var(--faint);
}

.examples button:hover {
  border-color: var(--accent);
}

.skel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skel-line {
  height: 7px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--surface-2) 0%, var(--line) 45%, var(--surface-2) 90%);
  background-size: 220% 100%;
  animation: shimmer 1.25s infinite linear;
}

.skel-line.w40 {
  width: 40%;
}
.skel-line.w70 {
  width: 70%;
}
.skel-line.w90 {
  width: 90%;
}

/* ── footer ──────────────────────────────────────── */

.foot {
  flex: 0 0 auto;
  border-top: 1px solid var(--line);
  padding: 7px 12px 9px;
  background: var(--panel);
}

.progress {
  height: 2px;
  border-radius: 2px;
  background: var(--line);
  overflow: hidden;
}

.progress span {
  display: block;
  height: 100%;
  background: var(--accent);
  transition: width 180ms ease;
}

.foot-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 9.5px;
  color: var(--muted);
}

.timing {
  font-variant-numeric: tabular-nums;
  color: var(--faint);
}

.fixture-note {
  margin: 4px 0 0;
  font-size: 8.5px;
  color: var(--faint);
  letter-spacing: 0.01em;
}

/* ── setup / onboarding ─────────────────────────── */

.setup-shell {
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 24px;
}

.setup-panel {
  height: min(880px, calc(100dvh - 48px));
}

body.is-extension .setup-shell {
  padding: 0;
}

body.is-extension .setup-panel {
  width: 100%;
  height: 100dvh;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.setup-top {
  height: 44px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
}

.setup-step {
  font-size: 9px;
  color: var(--faint);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 2px 7px;
  font-variant-numeric: tabular-nums;
}

.setup-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 30px 20px 18px;
}

.setup-kicker {
  margin: 0;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: var(--accent);
}

.setup-title {
  margin: 8px 0 10px;
  font-size: 22px;
  line-height: 1.28;
  letter-spacing: -0.02em;
  font-weight: 700;
}

.setup-lead {
  margin: 0;
  font-size: 12px;
  line-height: 1.65;
  color: var(--muted);
  word-break: keep-all;
}

.setup-tier-heading {
  margin: 16px 0 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--faint);
  text-transform: uppercase;
}

.setup-tiers {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.setup-tier {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 9px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;
}

.setup-tier:hover {
  border-color: var(--line-strong);
}

.setup-tier-active {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.setup-tier-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.setup-tier-label {
  font-size: 11.5px;
  font-weight: 650;
  line-height: 1.3;
}

.setup-tier-detail {
  font-size: 9.5px;
  color: var(--faint);
  line-height: 1.4;
}

.setup-tier-size {
  font-size: 9.5px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.setup-files {
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.setup-file {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto auto;
  gap: 8px;
  align-items: center;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  transition: border-color 160ms ease, opacity 160ms ease;
}

.setup-file-waiting {
  opacity: 0.62;
}

.setup-file-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
}

.setup-file-icon-model {
  background: var(--accent-soft);
  color: var(--accent);
}

.setup-file-icon-corpus {
  background: var(--case-soft);
  color: var(--case);
}

.setup-file-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.setup-file-name {
  font-size: 11.5px;
  font-weight: 650;
  line-height: 1.3;
}

.setup-file-meta {
  font-size: 9.5px;
  color: var(--faint);
}

.setup-volume-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}

.setup-volume-action {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border: 1px solid var(--line);
  border-radius: 5px;
  background: transparent;
  color: var(--muted);
  font-size: 8.5px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

.setup-volume-action:hover {
  border-color: var(--line-strong);
  color: var(--ink);
}

.setup-volume-file {
  position: relative;
  overflow: hidden;
}

.setup-volume-file input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.setup-file-error {
  border-color: var(--case);
}

.status-error {
  color: var(--case);
  font-weight: 700;
}

.setup-file-status {
  font-size: 9px;
  color: var(--faint);
  white-space: nowrap;
}

.status-done {
  color: var(--pass);
  font-weight: 700;
}

.status-active {
  color: var(--accent);
  font-weight: 700;
}

.setup-file-size {
  font-size: 9px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}

.setup-total {
  margin-top: 11px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 9.5px;
  color: var(--faint);
}

.setup-progress {
  margin-top: 20px;
}

.setup-track {
  height: 5px;
  border-radius: 3px;
  background: var(--line);
  overflow: hidden;
}

.setup-fill {
  display: block;
  height: 100%;
  background: var(--accent);
  border-radius: 3px;
  transition: width 180ms linear;
}

.setup-stats {
  margin-top: 7px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 9.5px;
  color: var(--faint);
  font-variant-numeric: tabular-nums;
}

.setup-actions {
  margin-top: 22px;
  display: flex;
  gap: 8px;
}

.setup-primary {
  flex: 1 1 auto;
  height: 38px;
  border: 0;
  border-radius: 9px;
  background: var(--ink);
  color: var(--panel);
  font-size: 12px;
  font-weight: 700;
}

.setup-primary:disabled {
  opacity: 0.4;
  cursor: default;
}

.setup-secondary {
  flex: 0 0 auto;
  height: 38px;
  padding: 0 13px;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  background: transparent;
  color: var(--muted);
  font-size: 11px;
}

.setup-ready-icon {
  width: 46px;
  height: 46px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--pass-soft);
  color: var(--pass);
  display: grid;
  place-items: center;
  font-size: 20px;
  font-weight: 700;
}

.setup-error-box {
  margin-top: 18px;
  padding: 12px;
  border: 1px solid color-mix(in srgb, #c0392b 26%, transparent);
  background: color-mix(in srgb, #c0392b 8%, transparent);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.setup-error-box strong {
  font-size: 11.5px;
  color: color-mix(in srgb, #c0392b 78%, var(--ink));
}

.setup-error-box span {
  font-size: 10px;
  color: var(--muted);
}

.setup-foot {
  flex: 0 0 auto;
  border-top: 1px solid var(--line);
  padding: 9px 16px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 8.5px;
  color: var(--faint);
}

/* ── motion ──────────────────────────────────────── */

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes shimmer {
  from {
    background-position: 120% 0;
  }
  to {
    background-position: -120% 0;
  }
}

@keyframes blink {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* scrollbars */
.dossier-wrap,
.original {
  scrollbar-width: thin;
  scrollbar-color: var(--line-strong) transparent;
}
.dossier-wrap::-webkit-scrollbar,
.original::-webkit-scrollbar {
  width: 6px;
}
.dossier-wrap::-webkit-scrollbar-thumb,
.original::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 3px;
}

/* ── 판례 전문 리더 ─────────────────────────────────── */

.result-text-hint {
  align-self: flex-start;
  margin-top: 1px;
  font-size: 9px;
  font-weight: 700;
  color: var(--accent);
}

.case-reader {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  background: var(--panel);
  animation: case-reader-in 140ms ease-out;
}

@keyframes case-reader-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.case-reader-head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: var(--panel);
  border-bottom: 1px solid var(--line);
}

.case-reader-back {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink-2);
  font-size: 15px;
  line-height: 1;
}

.case-reader-meta {
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.case-reader-copy {
  padding: 5px 9px;
  border: 1px solid var(--accent);
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
}

.case-reader-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--line-strong) transparent;
}

.case-reader-scroll::-webkit-scrollbar {
  width: 6px;
}

.case-reader-scroll::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 3px;
}

.case-reader-content {
  padding: 18px 18px 40px;
}

.case-reader-count {
  max-width: 34em;
  margin: 0 auto 12px;
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
}

.case-reader-text {
  max-width: 34em;
  margin: 0 auto;
  font-size: 15px;
  line-height: 1.7;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: keep-all;
}

.case-reader-note {
  max-width: 34em;
  margin: 24px auto;
  font-size: 12px;
  color: var(--muted);
}

.case-reader-missing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 34em;
  margin: 48px auto;
  text-align: center;
}

.case-reader-missing-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-2);
}

.case-reader-missing-detail {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.55;
  color: var(--muted);
}

.case-reader-install {
  margin-top: 6px;
  padding: 7px 12px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

/* 리더에서 연 SetupPanel 오버레이(App 상태 보존) */
.setup-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  overflow: auto;
  background: var(--bg);
}

/* ── 인용 바구니 & 넓은 워크스페이스 ─────────────────────
   폭 판정은 App이 .shell의 실제 폭(760px)으로 한다.
   데모 프레임이 400/820px로 컨테이너를 제한하므로 뷰포트 미디어쿼리를 쓰지 않는다. */

.shell[data-layout="narrow"] {
  padding: 12px;
}

.shell[data-layout="narrow"] .panel {
  width: min(400px, 100%);
  height: min(880px, calc(100dvh - 24px));
}

@media (max-width: 520px) {
  .shell[data-layout="narrow"] {
    padding: 0;
  }
  .shell[data-layout="narrow"] .panel {
    width: 100%;
    height: 100dvh;
    border: 0;
    border-radius: 0;
  }
}

.shell[data-layout="wide"] {
  padding: 0;
  display: block;
}

.shell[data-layout="wide"] .panel {
  width: 100%;
  height: 100dvh;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* 확장 사이드패널은 폭과 무관하게 꽉 채운다. */
body.is-extension .shell {
  padding: 0;
}

body.is-extension .shell .panel {
  width: 100%;
  height: 100dvh;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* ── result row (체크박스 + 선택) ───────────────────── */

.result-row {
  display: flex;
  align-items: flex-start;
}

.result-row.has-check {
  gap: 2px;
  padding-left: 8px;
}

.result-row .result-head-button {
  flex: 1 1 auto;
  min-width: 0;
}

.result-check {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  padding: 12px 2px 0 0;
  cursor: pointer;
}

.result-check input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: var(--accent);
  cursor: pointer;
}

.result.is-selected {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 16%, transparent);
}

/* ── wide top bar ─────────────────────────────────── */

.ws-top {
  position: sticky;
  top: 0;
  z-index: 10;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  background: var(--panel);
  border-bottom: 1px solid var(--line);
}

.ws-search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 36px;
  padding: 0 5px 0 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.ws-search:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent);
}

.ws-search input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 13px;
}

.ws-search input::placeholder {
  color: var(--faint);
}

.ws-search .send {
  width: 26px;
  height: 26px;
}

.ws-basket-button {
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink-2);
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;
}

.ws-basket-button b {
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

.ws-basket-button[aria-expanded="true"] {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--accent);
}

/* ── wide body ────────────────────────────────────── */

.ws-body {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(320px, 38%) minmax(0, 1fr);
}

.ws-list {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--line);
  background: var(--panel);
}

.ws-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--line-strong) transparent;
}

.ws-list-scroll::-webkit-scrollbar {
  width: 6px;
}

.ws-list-scroll::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 3px;
}

.ws-list-scroll.is-stale,
.ws-single.is-stale {
  opacity: 0.42;
  filter: saturate(0.5);
  pointer-events: none;
}

.ws-list .results {
  padding: 12px;
}

.ws-single {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
  transition: opacity 140ms ease;
}

.ws-single .dossier {
  max-width: 880px;
  margin: 0 auto;
}

.ws-empty {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  place-items: center;
  overflow-y: auto;
  padding: 24px;
}

.ws-empty .empty {
  width: 100%;
  max-width: 520px;
  padding-top: 0;
}

.ws-foot {
  flex: 0 0 auto;
}

/* ── wide detail pane ─────────────────────────────── */

.ws-detail {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface);
}

.ws-detail-pane {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ws-detail-head {
  position: sticky;
  top: 0;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 14px 18px 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
}

.ws-detail-kicker {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 10px;
  color: var(--muted);
}

.ws-detail-kicker .kind {
  width: 17px;
  height: 17px;
  font-size: 9px;
}

.ws-detail-kicker-label {
  font-weight: 700;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.ws-detail-meta {
  color: var(--faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ws-detail-title {
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}

.ws-detail-actions {
  display: flex;
  gap: 6px;
  margin-top: 2px;
}

.basket-add,
.basket-copy-one {
  height: 26px;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink-2);
  font-size: 10.5px;
  font-weight: 650;
}

.basket-add {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--accent);
}

.basket-add.is-added {
  background: var(--accent);
  color: #fff;
}

.ws-detail-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--line-strong) transparent;
}

.ws-detail-scroll::-webkit-scrollbar {
  width: 6px;
}

.ws-detail-scroll::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 3px;
}

.ws-detail-body {
  max-width: 44em;
  padding: 18px 18px 40px;
}

.ws-detail-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.72;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: keep-all;
}

.ws-detail-count {
  margin: 0 0 12px;
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
}

.ws-detail-note {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
}

.ws-detail-missing {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface-2);
}

.ws-detail-missing-title {
  margin: 0;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-2);
}

.ws-detail-missing-detail {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.55;
  color: var(--muted);
}

.ws-detail-missing .case-reader-install {
  align-self: flex-start;
  margin: 0;
}

.ws-detail-hint {
  margin: 20px 0 0;
  font-size: 10px;
  line-height: 1.5;
  color: var(--faint);
}

.ws-detail-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 32px;
  text-align: center;
}

.ws-detail-empty-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-2);
}

.ws-detail-empty-sub {
  margin: 0;
  max-width: 26em;
  font-size: 11.5px;
  line-height: 1.55;
  color: var(--muted);
  word-break: keep-all;
}

/* ── basket bar / pill ────────────────────────────── */

.basket-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--surface-2);
  border-top: 1px solid var(--line);
}

.basket-count {
  margin-right: auto;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}

.basket-action {
  height: 26px;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink-2);
  font-size: 10.5px;
  font-weight: 650;
  white-space: nowrap;
}

.basket-action-primary {
  border-color: var(--ink);
  background: var(--ink);
  color: var(--panel);
}

.basket-action:disabled {
  opacity: 0.36;
  cursor: default;
}

.basket-pill {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 48px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 8px 7px 14px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--panel);
  box-shadow: 0 10px 26px rgba(24, 26, 30, 0.24);
  animation: rise 160ms ease both;
}

.basket-pill .basket-count {
  color: var(--panel);
}

.basket-pill .basket-action {
  border-color: transparent;
  background: rgba(255, 255, 255, 0.14);
  color: var(--panel);
}

.basket-pill .basket-action-primary {
  background: var(--panel);
  color: var(--ink);
}

/* ── basket menu ──────────────────────────────────── */

.ws-basket-menu {
  position: absolute;
  top: 58px;
  right: 14px;
  z-index: 25;
  width: min(360px, calc(100% - 28px));
  max-height: min(50vh, 420px);
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: 12px;
  box-shadow: var(--shadow);
}

.ws-basket-menu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px 8px;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-2);
}

.ws-basket-close {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  font-size: 15px;
  line-height: 1;
}

.ws-basket-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ws-basket-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 7px 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-2);
}

.ws-basket-item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ws-basket-item-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.ws-basket-item-cite {
  font-size: 10px;
  color: var(--muted);
  overflow-wrap: anywhere;
}

.ws-basket-item-quote {
  font-size: 10px;
  color: var(--faint);
  overflow-wrap: anywhere;
}

.ws-basket-remove {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: var(--faint);
  font-size: 14px;
  line-height: 1;
}

.ws-basket-remove:hover {
  background: var(--line);
  color: var(--ink);
}

.ws-basket-empty {
  margin: 6px 2px 10px;
  font-size: 11px;
  color: var(--muted);
}

.ws-basket-menu-foot {
  display: flex;
  gap: 6px;
  padding-top: 10px;
}

.ws-basket-menu-foot .basket-action {
  flex: 1 1 0;
}

/* 760px 부근에서 상단 바가 넘치지 않게 */
@media (max-width: 860px) {
  .ws-top {
    gap: 8px;
    padding: 0 10px;
  }
  .ws-top .beta {
    display: none;
  }
  .ws-basket-button {
    padding: 0 9px;
  }
}

@media (max-width: 800px) {
  .ws-top .backend {
    display: none;
  }
}
